// Der Plus-Knopf auf jeder Seite — dieselben fuenf Formulare wie im Firmen-Verlauf.
//
// Die Firma der offenen Seite (usePageCompany bzw. die Adresszeile) wird
// UEBERNOMMEN, das Formular geht direkt auf — sie ist ohnehin fast immer die
// richtige, und der Auswahlschritt davor war damit fast immer ein Klick zu viel.
//
// Gewechselt wird im Formular: Termin, Notiz und Aufgabe tragen die Firmensuche
// selbst (Pflichtfeld), Anruf und Mail einen Knopf im Fuss, der das Fenster
// schliesst und die Auswahl aufmacht.
//
// Die Auswahl (ActivityCompanyModal) bleibt fuer die drei Faelle, in denen es etwas
// zu entscheiden gibt: mehrere Firmen auf der Seite (Sendung, Luftfracht), der
// Firmenwechsel aus Anruf/Mail heraus, und der Anruf ohne Firma — er braucht die
// Nummernliste, ohne Firma hat er nichts zu zeigen. Termin, Notiz, Aufgabe und Mail
// gehen ohne Vorschlag direkt auf und fragen im Formular.
//
// Steht schon ein Verlauf auf der Seite (Firmenakte, Kundenanfrage), bringt der
// seinen eigenen Knopf mit — samt Vorgang und sofortigem Eintrag im Verlauf; dieser
// tritt dann zurueck (useSpeedDialTaken).
import React, { useEffect, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import ActivitySpeedDial, { useSpeedDialTaken } from "./ActivitySpeedDial";
import ActivityCompanyModal, {
  ALLOWED_TYPES,
  loadCompany,
  loadCompanyContacts,
} from "./ActivityCompanyModal";
import { needsCompanyPicker, usePageCompanyValue } from "./pageCompany";
import CallContactSelectModal from "./CallContactSelectModal";
import ActivityFormModal from "./ActivityFormModal";
import NoteFormModal from "./NoteFormModal";
import TaskFormModal from "./TaskFormModal";
import EmailComposeModal from "./EmailComposeModal";
import Restricted from "../helpers/permission/Restricted";
import { openCallPanel } from "./telephony/CallPanel";
import { findByNumber } from "../helpers/telephony";

const OPTIONAL_COMPANY = ["task", "email"];

// Steht die Firma in der Adresszeile, ist sie der Schnellzugriff — die Detailseite
// ist derselbe Pfad fuer Kunde und Lead; die uebrigen /customer/*-Pfade fangen
// dieselbe Regel ab und sind deshalb ausgenommen.
const RESERVED_SEGMENTS = ["create", "edit", "merge"];

export const companyIdFromPath = (pathname) => {
  const match =
    matchPath(pathname, { path: "/customer/:companyId" }) ||
    matchPath(pathname, { path: "/companySettings/:companyId" });
  const id = match?.params?.companyId;
  return id && !RESERVED_SEGMENTS.includes(id) ? id : null;
};

const GlobalActivitySpeedDial = () => {
  const { t } = useTranslation(["components", "common", "tasks"]);
  const taken = useSpeedDialTaken();
  const [action, setAction] = useState(null);
  const [picking, setPicking] = useState(false);
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const pageCompany = usePageCompanyValue();
  const pathCompanyId = companyIdFromPath(location.pathname);
  const suggested = pageCompany || (pathCompanyId ? [{ id: pathCompanyId }] : null);

  const reset = () => {
    setAction(null);
    setPicking(false);
    setCompany(null);
    setContacts([]);
  };

  const start = async (next) => {
    setCompany(null);
    setContacts([]);
    setAction(next);

    const only = suggested?.length === 1 ? suggested[0] : null;
    if (!only) {
      setPicking(needsCompanyPicker(next, suggested));
      return;
    }

    setPicking(false);
    setLoading(true);
    try {
      // Ohne Namen ist die Firma nur eine ID aus der Adresszeile — erst das Laden
      // sagt, ob ueberhaupt ein Kunde oder Lead dahintersteht.
      const resolved = only.name ? only : await loadCompany(only.id);
      if (!resolved?.name || (resolved.type && !ALLOWED_TYPES.includes(resolved.type))) {
        // Kein Kunde/Lead (Lieferant, Spediteur) — der Vorschlag faellt weg.
        setPicking(needsCompanyPicker(next, null));
        return;
      }
      setContacts(await loadCompanyContacts(only.id));
      setCompany({ id: only.id, name: resolved.name });
    } catch (error) {
      console.error("GlobalActivitySpeedDial: company load failed", error);
      setPicking(needsCompanyPicker(next, null));
    } finally {
      setLoading(false);
    }
  };

  // Die Kontakte holt weder die Suche noch die Adresszeile mit; Anruf-, Termin- und
  // Mail-Fenster brauchen sie.
  const pick = async ({ id, name }) => {
    setLoading(true);
    try {
      setContacts(await loadCompanyContacts(id));
      setCompany({ id, name });
      setPicking(false);
    } catch (error) {
      console.error("GlobalActivitySpeedDial: company load failed", error);
      toast.error(t("Could not load the company."));
    } finally {
      setLoading(false);
    }
  };

  // Der AGFEO-Sprung: /call/:number leitet auf die Firma weiter und haengt die
  // Nummer des Anrufers als ?callFrom an. Dann steht das Protokollfenster sofort
  // offen — der Anruf laeuft ja schon, ein Klick weniger ist hier der ganze Punkt.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const callFrom = params.get("callFrom");
    if (!callFrom || !pathCompanyId) return;

    // Zuerst aus der Adresszeile nehmen: ein Neuladen oder ein Zurueck soll
    // dasselbe Fenster nicht ein zweites Mal aufmachen.
    params.delete("callFrom");
    const rest = params.toString();
    history.replace(location.pathname + (rest ? `?${rest}` : ""));

    Promise.all([loadCompany(pathCompanyId), loadCompanyContacts(pathCompanyId)])
      .then(([loadedCompany, loadedContacts]) => {
        if (!loadedCompany) return;
        openCallPanel({
          direction: "inbound",
          dialResult: { number: callFrom, dialedAt: new Date().toISOString() },
          company: loadedCompany,
          contacts: loadedContacts,
          companyID: loadedCompany.id,
          // Die Anlage liefert nur die Nummer — wer angerufen hat, faellt daraus.
          contactPersonId: findByNumber(callFrom, loadedContacts)?.id,
          unknownNumber: callFrom,
        });
      })
      .catch((error) => {
        console.error("GlobalActivitySpeedDial: callFrom load failed", error);
      });
    // Genau einmal je Adresse mit ?callFrom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, pathCompanyId]);

  if (taken) return null;

  return (
    <Restricted to="customers.read" or="assignedCustomers.read">
      <ActivitySpeedDial
        global
        onNewCall={() => start("call")}
        onNewMeeting={() => start("meeting")}
        onNewNote={() => start("note")}
        onNewTask={() => start("task")}
        onNewEmail={() => start("email")}
      />

      {picking && (
        <ActivityCompanyModal
          suggested={suggested}
          onPick={pick}
          busy={loading}
          onClose={reset}
          onSkip={
            OPTIONAL_COMPANY.includes(action) ? () => setPicking(false) : undefined
          }
        />
      )}

      {action === "call" && company && !picking && (
        <CallContactSelectModal
          company={company}
          contacts={contacts}
          onChangeCompany={() => setPicking(true)}
          onClose={reset}
        />
      )}

      {/* `!loading` haelt das Fenster zurueck, bis die uebernommene Firma steht: es
          liest sie beim Aufbau EINMAL in seinen Zustand, spaeter nachgereicht kaeme
          sie nicht mehr an. */}
      {action === "meeting" && !picking && !loading && (
        <ActivityFormModal company={company} contacts={contacts} onClose={reset} />
      )}

      {action === "note" && !picking && !loading && (
        <NoteFormModal company={company} onClose={reset} />
      )}

      {action === "task" && !picking && !loading && (
        <TaskFormModal companyId={company?.id} company={company} onClose={reset} />
      )}

      {action === "email" && !picking && !loading && (
        <EmailComposeModal
          company={company}
          contacts={contacts}
          onChangeCompany={() => setPicking(true)}
          onClose={reset}
        />
      )}
    </Restricted>
  );
};

export default GlobalActivitySpeedDial;
